import {
  MAX_RECURRENCE_COUNT,
  RECURRENCE_DEFAULT_HORIZON_MONTHS,
  type RecurrenceFrequency,
  type RecurrenceRule,
} from "../../../../types/api";
import { utcDateInputValue } from "../../../../utils/calendarTime";
import { formatDayKey } from "./eventDateRange";

/**
 * Pure helpers for the repeat rule on the event editor. Kept out of the component file so both the
 * editor and its tests can use them without tripping react-refresh/only-export-components.
 */

/**
 * The default a coach gets the moment they tick "Repeat": weekly, every week, no end date.
 *
 * No end date is the honest default because it is the answer a coach actually has — a weekly
 * practice starts now and runs "until the season's over", a date they usually cannot name yet.
 * The server materializes {@link RECURRENCE_DEFAULT_HORIZON_MONTHS} months of it.
 */
export const DEFAULT_RECURRENCE: RecurrenceRule = {
  frequency: "Weekly",
  interval: 1,
  untilDateUtc: null,
};

/** The frequency options, with the noun each one steps by. */
export const FREQUENCY_OPTIONS: { value: RecurrenceFrequency; label: string; unit: string }[] = [
  { value: "Daily", label: "Daily", unit: "day" },
  { value: "Weekly", label: "Weekly", unit: "week" },
  { value: "Monthly", label: "Monthly", unit: "month" },
];

const unitFor = (frequency: RecurrenceFrequency): string =>
  FREQUENCY_OPTIONS.find((o) => o.value === frequency)?.unit ?? "week";

/** The seven weekday toggles, Sunday first, numbered like `Date.getDay()` and the API. */
export const WEEKDAY_OPTIONS: { value: number; short: string; label: string }[] = [
  { value: 0, short: "Sun", label: "Sunday" },
  { value: 1, short: "Mon", label: "Monday" },
  { value: 2, short: "Tue", label: "Tuesday" },
  { value: 3, short: "Wed", label: "Wednesday" },
  { value: 4, short: "Thu", label: "Thursday" },
  { value: 5, short: "Fri", label: "Friday" },
  { value: 6, short: "Sat", label: "Saturday" },
];

/**
 * The weekday (0 = Sunday) a YYYY-MM-DD day key falls on, or null when there is no usable day yet.
 * Read at UTC noon so the answer is the calendar day itself, whatever zone the browser is in.
 */
export function weekdayOfDayKey(dayKey: string | undefined): number | null {
  if (!dayKey || !/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return null;
  const date = new Date(`${dayKey}T12:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date.getUTCDay();
}

/**
 * The days a weekly series will actually run on: whatever the coach picked plus the day the event
 * starts, sorted and without repeats. The start day is always in, because the event being created
 * is the first occurrence. The coach's picks are kept apart from it, so moving the start date
 * swaps the locked day instead of leaving the old one switched on.
 */
export function effectiveWeekdays(picked: number[] | undefined, startDay: number | null): number[] {
  const days = new Set((picked ?? []).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6));
  if (startDay !== null) days.add(startDay);
  return [...days].sort((a, b) => a - b);
}

/** Flips one weekday in the coach's picks. The start day is locked on, so it never flips. */
export function toggleWeekday(
  picked: number[] | undefined,
  day: number,
  startDay: number | null,
): number[] {
  if (day === startDay) return effectiveWeekdays(picked, startDay);
  const current = effectiveWeekdays(picked, startDay);
  const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
  return next.sort((a, b) => a - b);
}

/**
 * The rule as the API should receive it. A weekly repeat on more than one day carries its weekdays
 * with the start day included. Anything else sends none: daily and monthly because the server
 * refuses weekdays there, and the start day alone because that is exactly what a weekly repeat
 * already means. Leaving the field off in that case keeps the request identical to before weekdays
 * existed, so it also behaves the same on an API too old to know the field.
 */
export function recurrenceRequest(rule: RecurrenceRule, firstDayKey?: string): RecurrenceRule {
  const { daysOfWeek, ...rest } = rule;
  if (rule.frequency !== "Weekly") return rest;
  const days = effectiveWeekdays(daysOfWeek, weekdayOfDayKey(firstDayKey));
  return days.length > 1 ? { ...rest, daysOfWeek: days } : rest;
}

/** "Tue", "Tue and Thu", "Mon, Wed and Fri". */
function joinWeekdays(days: number[]): string {
  const names = days.map((d) => WEEKDAY_OPTIONS[d]?.short ?? "");
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The end date as a YYYY-MM-DD day key, or "" when the rule carries none. */
export function recurrenceEndDayKey(rule: RecurrenceRule): string {
  return rule.untilDateUtc ? utcDateInputValue(rule.untilDateUtc) : "";
}

/**
 * The readable reason a rule is rejected, or "" when it is fine.
 *
 * `firstDayKey` is the event's own first day (YYYY-MM-DD). It is needed because the end date is
 * only meaningful relative to it: the server treats an end on or before the first day as a
 * one-occurrence series, so both are refused here rather than silently accepted. There is
 * deliberately no occurrence-count check left here — the count is derived server-side from the
 * date range, the series timezone and the first start, none of which the client holds. A range
 * implying more than {@link MAX_RECURRENCE_COUNT} occurrences is refused there with a message
 * this form surfaces verbatim.
 */
export function validateRecurrence(rule: RecurrenceRule, firstDayKey?: string): string {
  if (!Number.isInteger(rule.interval) || rule.interval < 1) {
    return "Repeat every 1 or more, so a whole number of days, weeks, or months.";
  }
  const endKey = recurrenceEndDayKey(rule);
  // <=, not <. An end date ON the first day yields a "repeating" event with exactly one
  // occurrence, which is not what someone who ticked Repeat asked for and which the server would
  // accept silently.
  if (endKey && firstDayKey && endKey <= firstDayKey) {
    return "The repeat end date has to be after the event's first day.";
  }
  return "";
}

/**
 * "Every week until Oct 31, 2026." / "Every 2 weeks." / "Weekly on Tue and Thu until Oct 21, 2026."
 * The plain-English echo under the fields.
 *
 * Weekdays are named only once there is more than one, since a weekly repeat on the start day alone
 * is the plain "Every week" it always was. `firstDayKey` supplies that start day, which counts as
 * selected even before the coach touches a toggle.
 *
 * A rule with no end date says nothing about how long it runs; {@link describeRecurrenceHorizon}
 * is the sentence that follows it, because "we'll keep going" and "we'll create a year of these"
 * are very different promises.
 */
export function describeRecurrence(rule: RecurrenceRule, firstDayKey?: string): string {
  const unit = unitFor(rule.frequency);
  const days =
    rule.frequency === "Weekly"
      ? effectiveWeekdays(rule.daysOfWeek, weekdayOfDayKey(firstDayKey))
      : [];

  let every: string;
  if (days.length > 1) {
    const cadence = rule.interval === 1 ? "Weekly" : `Every ${rule.interval} weeks`;
    every = `${cadence} on ${joinWeekdays(days)}`;
  } else {
    every = rule.interval === 1 ? `Every ${unit}` : `Every ${rule.interval} ${unit}s`;
  }

  const endKey = recurrenceEndDayKey(rule);
  return endKey ? `${every} until ${formatDayKey(endKey)}.` : `${every}.`;
}

/**
 * What a blank end date will actually do, or "" when one was given.
 *
 * Occurrences are materialized as real calendar rows, so "no end date" cannot mean forever. Saying
 * so is not pedantry: a coach who reads a blank field as "repeats indefinitely" discovers the truth
 * a year later, when the practice silently stops appearing.
 */
export function describeRecurrenceHorizon(rule: RecurrenceRule): string {
  if (recurrenceEndDayKey(rule)) return "";
  return (
    `With no end date we'll add ${RECURRENCE_DEFAULT_HORIZON_MONTHS} months of these, ` +
    `up to ${MAX_RECURRENCE_COUNT} in a series.`
  );
}

/**
 * The count line under the selected delete option in the scope prompt ("Deletes 12 events"). Null when
 * there is no trustworthy number, in which case the prompt shows nothing rather than a guess.
 */
export function deleteCountLabel(count: number | null | undefined): string | null {
  if (typeof count !== "number" || !Number.isFinite(count) || count < 1) return null;
  return count === 1 ? "Deletes 1 event" : `Deletes ${count} events`;
}
